import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserProfilePage from '../../UserProfilePage';
import McaSessionsSection from './McaSessionsSection';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import {
  AUTH_STORAGE_KEY,
  REFRESH_STORAGE_KEY,
  getDefaultOrgStorageKey,
} from '../../constants';
import {
  refreshToken,
  saveCurrentUserProfile,
} from '../../hooks/useAuth';
import { getCurrentUserInternalRole, getUserRoles } from '../../hooks/useUsers';
import type {
  ProfileSaveInput,
  SessionUser,
} from '../../types/index';
import { parseJwtPayload } from '../../utils/authTokens';
import { resolveRoleActions, roleToActionList } from '../../utils/permissions';
import { internalRoleToPermissionSet } from '../../utils/permissionsInternal';
import { getErrorMessage } from '../../utils/requestErrors';

export default function ProfileContainer() {
  const navigate = useNavigate();
  const {
    currentUser,
    org,
    updateCurrentUserProfile,
  } = useLegacyAppSession();
  const [resolvedRole, setResolvedRole] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fallbackRole = String(currentUser?.role || '').trim();

    setResolvedRole(fallbackRole);

    if (!org?.id) {
      return () => {
        cancelled = true;
      };
    }

    getUserRoles(org.id)
      .then((roles) => {
        if (cancelled) return;
        const nextRole = String(roles?.[0] || fallbackRole || 'viewer').trim();
        setResolvedRole(nextRole || 'viewer');
      })
      .catch((error) => {
        if (cancelled) return;
        setResolvedRole(fallbackRole || 'viewer');
        if (error?.status !== 403) {
          showToastError('Could not load user role');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.role, org?.id]);

  const profileUser = useMemo(() => {
    if (!currentUser) {
      return currentUser;
    }

    return {
      ...currentUser,
      role: resolvedRole || currentUser.role || 'viewer',
    };
  }, [currentUser, resolvedRole]);

  return (
    <UserProfilePage
      user={profileUser}
      organization={org}
      extraSection={<McaSessionsSection />}
      onSave={async (updatedUser: ProfileSaveInput) => {
        try {
          const { emailChanged, updatedUser: savedUser } = await saveCurrentUserProfile(updatedUser, currentUser);
          try {
            if (savedUser?.id) {
              const storageKey = getDefaultOrgStorageKey(savedUser.id);
              if (savedUser.defaultOrganizationId) {
                window.localStorage.setItem(storageKey, String(savedUser.defaultOrganizationId));
              } else {
                window.localStorage.removeItem(storageKey);
              }
            }
          } catch {
            // ignore storage errors
          }

          if (emailChanged) {
            showToastSuccess('Email change saved. Please check your inbox to verify your new email address, then log in again.');
            try {
              window.localStorage.removeItem(AUTH_STORAGE_KEY);
            } catch {
              // ignore storage errors
            }
            try {
              window.localStorage.removeItem(REFRESH_STORAGE_KEY);
            } catch {
              // ignore storage errors
            }
            navigate('/login');
            return;
          }

          let nextUser: SessionUser = savedUser;
          let nextToken = '';
          const orgId = org?.id;
          if (!orgId) {
            throw new Error('An organization is required to refresh the session.');
          }
          const refreshData = await refreshToken(orgId);
          const payload = refreshData?.token
            ? parseJwtPayload(refreshData.token)
            : null;
          const isSuperUser = payload
            ? payload.IsSuperUser === 'True' || payload.IsSuperUser === true
            : Boolean(savedUser.isSuperUser ?? currentUser?.isSuperUser);
          let role = String(savedUser.role || currentUser?.role || 'viewer')
            .trim()
            .toLowerCase() || 'viewer';
          let organizationRoleActions = [
            ...(currentUser?.permissions?.actions || []),
          ];

          try {
            const roles = await getUserRoles(orgId);
            role = String(roles?.[0] || role).trim().toLowerCase() || 'viewer';
            organizationRoleActions = roleToActionList(role);
          } catch {
            if (!organizationRoleActions.length) {
              organizationRoleActions = roleToActionList(role);
            }
          }

          let internalRole = isSuperUser ? String(currentUser?.internalRole || '') : '';
          let internalRoleActions = isSuperUser
            ? [...(currentUser?.internalRoleActions || [])]
            : [];

          if (isSuperUser) {
            try {
              internalRole = await getCurrentUserInternalRole();
              internalRoleActions = internalRoleToPermissionSet(internalRole, true);
            } catch {
              // Preserve the last resolved internal role if refreshing it fails.
            }
          }

          const roleActions = resolveRoleActions({
            role,
            organizationActions: organizationRoleActions,
            internalRole,
          });

          const permissions = {
            organizationId: String(orgId),
            actions: organizationRoleActions,
          };

          if (refreshData?.token) {
            nextToken = refreshData.token;
            if (payload) {
              nextUser = {
                id: Number(payload.Id || payload.id || 0) || 0,
                name: payload.Name || payload.name || savedUser.name,
                email: payload.Email || payload.email || savedUser.email,
                role,
                org: savedUser.org || currentUser?.org || '',
                isSuperUser,
                internalRole,
                internalRoleActions,
                permissions,
                roleActions,
                defaultOrganizationId: savedUser.defaultOrganizationId !== undefined
                  ? savedUser.defaultOrganizationId
                  : currentUser?.defaultOrganizationId ?? null,
              };
            }
          } else {
            nextUser = {
              ...savedUser,
              role,
              isSuperUser,
              internalRole,
              internalRoleActions,
              permissions,
              roleActions,
              defaultOrganizationId: savedUser.defaultOrganizationId !== undefined
                ? savedUser.defaultOrganizationId
                : currentUser?.defaultOrganizationId ?? null,
            };
          }

          updateCurrentUserProfile(nextUser, nextToken);
          showToastSuccess('User profile successfully saved.');
          navigate('/products');
        } catch (error) {
          console.error('[ProfileContainer] Failed to save user profile:', error);
          showToastError(getErrorMessage(error, 'User profile could not be saved.'));
        }
      }}
      onCancel={() => navigate('/products')}
    />
  );
}
